import {
  createSearchkitClient,
  ESTransporter,
  isClientFaultError,
  purgeCacheOnFailure,
  Searchkit,
} from "@ui/lib/searchkit";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import {
  getSearchSession,
  type SearchRequestBody,
  type SearchRequestEnhancer,
  type SearchSessionClient,
  type SearchSessionKey,
  type SearchTransportCallbackTarget,
} from "@/lib/search-session-store";

type SearchkitConfig = ConstructorParameters<typeof Searchkit>[0];
export type SearchSettings = SearchkitConfig["search_settings"];
type ESTransporterCtorArgs = ConstructorParameters<typeof ESTransporter>;
type ESTransporterConfig = ESTransporterCtorArgs[0];
type ESTransporterSettings = ESTransporterCtorArgs[1];

interface UseSearchkitClientOptions {
  host: string;
  indexName: string;
  /** Hoist to module scope so the memo dep stays referentially stable. */
  searchSettings: SearchSettings;
  /**
   * Optional per-request body transformer wired into Searchkit's
   * `hooks.beforeSearch`. Caller must memoise to keep the memo dep stable.
   * Used by `/destinations` to inject the `_travily_scoring` block the
   * destinations search front door reads to build its ranking.
   */
  requestEnhancer?: SearchRequestEnhancer;
  /**
   * Reuse the search client across Inertia navigations via the module-scoped
   * session store. The transporter outlives any single mount, so its
   * callbacks route through the session's mutable callback target, which
   * every mount re-points at its own setters.
   */
  sessionKey?: SearchSessionKey;
}

// Searchkit's ESTransporter throws on any non-2xx HTTP response (before the
// body is parsed), and the instantsearch-client adapter then swallows the
// error into empty results, so `useInstantSearch().status` never flips to
// `"error"`. Intercept at the transporter layer to surface transport
// failures, and to optionally enhance each request body before it's
// serialised to NDJSON.
class TrackedTransporter extends ESTransporter {
  // In-flight dedup: an infinite-scroll burst re-requests the same page with a
  // byte-identical body before the first response lands; collapse those onto
  // one network call instead of stacking concurrent searches on the backend.
  private readonly inflight = new Map<string, ReturnType<ESTransporter["msearch"]>>();
  // Set on any failed msearch; consumed by the client wrapper in
  // `buildClientEntry` to purge the adapter's poisoned response cache.
  private failed = false;

  constructor(
    config: ESTransporterConfig,
    settings: ESTransporterSettings,
    private readonly callbacks: SearchTransportCallbackTarget,
  ) {
    super(config, settings);
  }

  consumeFailure(): boolean {
    const failed = this.failed;
    this.failed = false;
    return failed;
  }

  override async msearch(requests: Parameters<ESTransporter["msearch"]>[0]) {
    const enhance = this.callbacks.getEnhancer();
    const prepared = enhance
      ? requests.map((request) => ({
          ...request,
          body: enhance(request.body as SearchRequestBody) as typeof request.body,
        }))
      : requests;
    const key = JSON.stringify(prepared);
    const existing = this.inflight.get(key);
    if (existing) return existing;

    const pending = (async () => {
      try {
        const result = await super.msearch(prepared);
        this.callbacks.onSuccess();
        return result;
      } catch (error) {
        this.failed = true;
        // 4xx means the backend rejected the request as the client's fault
        // (422 request caps, 429 throttle): degrade to empty results without
        // flipping the transport-error UI. 5xx and network failures surface it.
        if (!isClientFaultError(error)) this.callbacks.onError();
        throw error;
      } finally {
        this.inflight.delete(key);
      }
    })();
    this.inflight.set(key, pending);
    return pending;
  }
}

export interface SearchTransportContextValue {
  isTransportError: boolean;
}

export const SearchTransportContext = createContext<SearchTransportContextValue>({ isTransportError: false });

export function useSearchTransportError(): boolean {
  return useContext(SearchTransportContext).isTransportError;
}

function buildClientEntry(config: {
  host: string;
  csrfToken: string;
  searchSettings: SearchSettings;
}): SearchSessionClient {
  const callbackTarget: SearchTransportCallbackTarget = {
    onSuccess: () => {},
    onError: () => {},
    getEnhancer: () => undefined,
  };

  const connection = new TrackedTransporter(
    {
      host: config.host,
      headers: {
        "content-type": "application/x-ndjson",
        "X-CSRF-Token": config.csrfToken,
      },
    },
    { debug: false },
    callbackTarget,
  );

  const sk = new Searchkit({ connection, search_settings: config.searchSettings });

  // This client is session-persisted, so a permanently-cached error result
  // would brick its search state for the whole visit — purge on failure.
  const searchClient = createSearchkitClient(sk);
  purgeCacheOnFailure(searchClient, () => connection.consumeFailure());

  return { searchClient, callbackTarget };
}

export function useSearchkitClient({
  host,
  indexName,
  searchSettings,
  requestEnhancer,
  sessionKey,
}: UseSearchkitClientOptions) {
  const csrfToken = useCsrfToken();
  const [isTransportError, setIsTransportError] = useState(false);
  // Latest-setter ref so the memoised transporter doesn't churn on re-render.
  const setIsTransportErrorRef = useRef(setIsTransportError);
  setIsTransportErrorRef.current = setIsTransportError;
  // Local snapshot of last-reported state so success/error callbacks dispatch
  // setState only on a true transition.
  const lastReportedRef = useRef(false);
  // Latest-enhancer ref so callers can swap the body transformer (e.g. when
  // the viewer's filter state changes) without rebuilding the search client.
  const requestEnhancerRef = useRef(requestEnhancer);
  requestEnhancerRef.current = requestEnhancer;

  const entry = useMemo(() => {
    if (sessionKey) {
      const session = getSearchSession(sessionKey);
      session.client ??= buildClientEntry({ host, csrfToken, searchSettings });
      return session.client;
    }
    return buildClientEntry({ host, csrfToken, searchSettings });
  }, [csrfToken, host, searchSettings, sessionKey]);

  // Re-point the (possibly session-reused) transporter at this mount's state.
  const mountToken = useRef({});
  const attach = useCallback(() => {
    entry.callbackTarget.owner = mountToken.current;
    entry.callbackTarget.onSuccess = () => {
      if (lastReportedRef.current) {
        lastReportedRef.current = false;
        setIsTransportErrorRef.current(false);
      }
    };
    entry.callbackTarget.onError = () => {
      if (!lastReportedRef.current) {
        lastReportedRef.current = true;
        setIsTransportErrorRef.current(true);
      }
    };
    entry.callbackTarget.getEnhancer = () => requestEnhancerRef.current;
  }, [entry]);

  // At RENDER, because InstantSearch issues its first search before effects
  // run: attaching in the effect alone would send that search with no
  // enhancer, and on `/destinations` that is the whole scoring block — a
  // shared link would rank as though its budget and window had not been named.
  attach();

  // Session entries outlive the page, so a dead mount must stop reporting into
  // its own state: `onSuccess`/`onError` dispatch setState, and left attached
  // they would push transport errors into an unmounted fiber.
  //
  // The enhancer is deliberately NOT detached. It only reads a ref, so calling
  // it after unmount is harmless, and detaching it opens a window in which
  // searches go out unenhanced: StrictMode mounts, tears down and remounts
  // without re-rendering in between, so the cleanup lands after the last
  // render-time attach and anything InstantSearch fires before the remount
  // loses the whole scoring block — which on `/destinations` is the budget and
  // the travel window, and a search without them silently ranks the wrong set.
  //
  // Re-attaching on mount covers the same cycle for the two setState callbacks.
  useEffect(() => {
    attach();
    const token = mountToken.current;
    return () => {
      if (entry.callbackTarget.owner !== token) return;
      entry.callbackTarget.owner = undefined;
      entry.callbackTarget.onSuccess = () => {};
      entry.callbackTarget.onError = () => {};
    };
  }, [attach, entry]);

  const searchTransport = useMemo<SearchTransportContextValue>(() => ({ isTransportError }), [isTransportError]);

  return {
    indexName,
    searchClient: entry.searchClient,
    searchTransport,
  };
}
